import type { ReactNode } from 'react';
import {
  pendingHotelSearchFields,
  type HotelResultsProps,
  type HotelSearchValue,
} from '@voll-intelligence/intelligence-ui-kit';
import { useGenUiI18n } from '../../../shared/ui/i18n.js';
import type { HotelPlannerBlock } from '@voll-intelligence/genui-contract';
import type { HotelStage } from './hotel_selection.js';
import type { HotelSearchPreviewState } from './use_hotel_search_preview.js';

/** Estado e aviso saem juntos: a condição de erro é a mesma, e mantê-la em duas
 *  funções já custou uma divergir da outra. O título e o tom viajam com a
 *  mensagem porque é o mesmo diagnóstico — timeout é falha transitória
 *  (`warning`), o resto é falha (`danger`).
 *
 *  `offersCount` entra por PARÂMETRO, e não como `ui.offers?.length` aqui dentro:
 *  esta é uma das leituras sensíveis a ofertas, e passa por
 *  `withDeterministicOffers` (ver a doc dele) — o modo determinístico com mais
 *  de 6 hotéis não decide "vazio/pronto" pela lista truncada do bloco de fio.
 *  M1 · 1.2 — o chamador passa a contagem JÁ FILTRADA por `directives`
 *  (`view.offers.length`, não `effectiveOffers.length`): uma diretiva do
 *  agente que não casa com nenhuma oferta em cena vira o MESMO aviso "nenhum
 *  hotel encontrado" de uma busca vazia, em vez de um dock silenciosamente
 *  vazio sob um estado "pronto". */
export function resultNotice(
  ui: HotelPlannerBlock,
  stage: HotelStage,
  previewState: HotelSearchPreviewState,
  t: ReturnType<typeof useGenUiI18n>['t'],
  offersCount: number,
): Pick<HotelResultsProps, 'state' | 'message' | 'noticeTitle' | 'noticeTone'> {
  if (previewState.phase === 'error' || (ui.searchError && !previewState.hasStartedSearch)) {
    const timedOut = ui.searchError === 'timeout';
    return {
      state: 'error',
      noticeTitle: t(
        timedOut ? 'hotelPlanner.timeoutErrorTitle' : 'hotelPlanner.providerErrorTitle',
      ),
      message: t(timedOut ? 'hotelPlanner.timeoutError' : 'hotelPlanner.providerError'),
      noticeTone: timedOut ? 'warning' : 'danger',
    };
  }
  if (stage === 'loading') return { state: 'loading', message: t('hotelPlanner.searching') };
  if (stage !== 'results') return { state: 'empty' };
  if (offersCount > 0) return { state: 'ready' };
  return {
    state: 'empty',
    noticeTitle: t('hotelPlanner.noResultsTitle'),
    message: t('hotelPlanner.noResults'),
  };
}

/** Mesmo diagnóstico do dock: `resultNotice` só devolve `state: 'empty'` com
 *  `stage === 'results'` no ramo "busca terminou sem ofertas" — o outro
 *  `empty`, antes da busca, tem `stage !== 'results'`. Extraído para a fala de
 *  abertura (`introKeyOf`) não recomputar a condição por conta própria e
 *  arriscar divergir do aviso do dock. */
export function isNoResults(notice: Pick<HotelResultsProps, 'state'>, stage: HotelStage): boolean {
  return notice.state === 'empty' && stage === 'results';
}

/** A fala que abre o card compacto — fora do organismo pelo teto de linhas.
 *  A FASE vem antes da completude: enquanto o dock está `loading` (esqueleto),
 *  afirmar "tudo pronto" ali lê como bug de cards vazios (#2444) — a fala diz que
 *  está buscando. O `state` é o MESMO que o aviso do dock resolve (`resultNotice`),
 *  então erro tem precedência sobre "carregando" nas duas falas — as duas não se
 *  contradizem. Fora do voo, a completude dos campos decide "falta algo" vs
 *  "tudo pronto".
 *
 *  `noResults` tem precedência DELIBERADA sobre pendência de campos: apagar
 *  uma data depois de uma busca vazia não some com o aviso — `resultNotice`
 *  faz o mesmo no dock (ignora pendência, mostra "Nenhum hotel encontrado"
 *  contanto que `stage` continue `results`). Uma fonte de verdade para
 *  "sem resultado", não duas que podem divergir. */
export function introKeyOf(
  searchValue: HotelSearchValue,
  state: HotelResultsProps['state'],
  noResults: boolean,
) {
  if (state === 'loading') return 'hotelPlanner.contextSearching' as const;
  if (noResults) return 'hotelPlanner.contextEmpty' as const;
  return pendingHotelSearchFields(searchValue).length
    ? ('hotelPlanner.contextPending' as const)
    : ('hotelPlanner.contextReady' as const);
}

/** A fala que abre o card. Só a CONVERSA a mostra — o organismo a monta sob
 *  `!page && dispatch`; a PÁGINA de BUSCA já traz o próprio `<h1>` e não conversa,
 *  então o subtítulo ali era ruído fora do padrão (#2401). Componente próprio (e
 *  não `<p>` inline) para o organismo não estourar o teto de linhas.
 *
 *  Recebe a CHAVE já resolvida (`introKeyOf`), calculada no organismo — o
 *  `noResults` que decide essa chave é o MESMO diagnóstico do dock
 *  (`resultNotice` — `stage === 'results'` com a lista vazia), e recomputar
 *  aqui dentro arriscaria as duas falas divergirem. */
export function HotelIntroLine({
  introKey,
}: {
  readonly introKey: ReturnType<typeof introKeyOf>;
}): ReactNode {
  const { t } = useGenUiI18n();
  return <p className="text-muted-foreground">{t(introKey)}</p>;
}
